import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaCity, FaBookOpen, FaUtensils, FaCalendarAlt, FaPlay, FaStar } from 'react-icons/fa';

interface CulturalDashboard {
  counts: {
    tehranLessons: number;
    poems: number;
    recipes: number;
    holidays: number;
  };
  recent: {
    tehranLessons: any[];
    poems: any[];
    recipes: any[];
    holidays: any[];
  };
}

const Cultural: React.FC = () => {
  const [dashboard, setDashboard] = useState<CulturalDashboard | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCulturalDashboard();
  }, []);

  const fetchCulturalDashboard = async () => {
    try {
      const response = await fetch('/api/cultural/dashboard');
      const data = await response.json();
      if (data.success) {
        setDashboard(data.data);
      }
    } catch (error) {
      console.error('Error fetching cultural dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const culturalFeatures = [
    {
      title: "A Day in Tehran",
      farsiTitle: "یک روز در تهران",
      description: "Experience daily life in Tehran through interactive dialogues and real conversations",
      icon: <FaCity className="text-3xl" />,
      color: "from-blue-500 to-cyan-500",
      link: "/cultural/tehran",
      count: dashboard?.counts.tehranLessons || 0
    },
    {
      title: "Hafez Poetry",
      farsiTitle: "اشعار حافظ",
      description: "Read and understand the beautiful poetry of Hafez with line-by-line translations",
      icon: <FaBookOpen className="text-3xl" />,
      color: "from-purple-500 to-pink-500",
      link: "/hafez",
      count: dashboard?.counts.poems || 0
    },
    {
      title: "Persian Cookbook",
      farsiTitle: "کتاب آشپزی ایرانی",
      description: "Learn Persian cooking with step-by-step recipes and cultural context",
      icon: <FaUtensils className="text-3xl" />,
      color: "from-orange-500 to-red-500",
      link: "/cultural/recipes",
      count: dashboard?.counts.recipes || 0
    },
    {
      title: "Holiday Packs",
      farsiTitle: "بسته‌های تعطیلات",
      description: "Celebrate Persian holidays with stories, phrases, and interactive activities",
      icon: <FaCalendarAlt className="text-3xl" />,
      color: "from-emerald-500 to-teal-500",
      link: "/cultural/holidays",
      count: dashboard?.counts.holidays || 0
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 dark:from-gray-900 dark:to-gray-800">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 dark:from-gray-900 dark:to-gray-800">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold dark:text-white mb-4">
            Cultural Immersion
          </h1>
          <p className="text-xl dark:text-gray-300 mb-2">
            Immerse yourself in Persian culture
          </p>
          <p className="text-lg dark:text-gray-400">
            Experience the rich traditions, poetry, cuisine, and daily life of Iran
          </p>
        </div>

        {/* Main Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {culturalFeatures.map((feature, index) => (
            <Link
              key={index}
              to={feature.link}
              className="group block"
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 dark:border-gray-700">
                <div className={`w-16 h-16 rounded-xl bg-gradient-to-r ${feature.color} flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  {feature.icon}
                </div>
                
                <h3 className="text-xl font-bold dark:text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm dark:text-gray-400 mb-2">
                  {feature.farsiTitle}
                </p>
                <p className="dark:text-gray-300 text-sm mb-4">
                  {feature.description}
                </p>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm dark:text-gray-400">
                    {feature.count} lessons available
                  </span>
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center text-white text-sm group-hover:scale-110 transition-transform duration-300">
                    <FaPlay className="text-xs" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Recent Content */}
        {dashboard && (
          <div className="space-y-8">
            {/* Recent Tehran Lessons */}
            {dashboard.recent.tehranLessons.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center">
                    <FaCity className="mr-2 text-blue-500" />
                    Recent Tehran Lessons
                  </h2>
                  <Link
                    to="/cultural/tehran"
                    className="text-orange-500 hover:text-orange-600 font-medium"
                  >
                    View All
                  </Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {dashboard.recent.tehranLessons.map((lesson: any) => (
                    <Link
                      key={lesson._id}
                      to={`/cultural/tehran/${lesson._id}`}
                      className="block p-4 rounded-lg border border-gray-100 dark:border-gray-700 hover:border-orange-300 transition-colors"
                    >
                      <h3 className="font-semibold text-gray-800 dark:text-white mb-2">
                        {lesson.title}
                      </h3>
                      <p className="text-sm dark:text-gray-300 mb-2">
                        {lesson.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                          {lesson.category}
                        </span>
                        <span className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded">
                          {lesson.difficulty}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Poems */}
            {dashboard.recent.poems.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center">
                    <FaBookOpen className="mr-2 text-purple-500" />
                    Recent Hafez Poems
                  </h2>
                  <Link
                    to="/hafez"
                    className="text-orange-500 hover:text-orange-600 font-medium"
                  >
                    View All
                  </Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {dashboard.recent.poems.map((poem: any) => (
                    <Link
                      key={poem._id}
                      to={`/cultural/poetry/${poem._id}`}
                      className="block p-4 rounded-lg border border-gray-100 dark:border-gray-700 hover:border-orange-300 transition-colors"
                    >
                      <h3 className="font-semibold text-gray-800 dark:text-white mb-2">
                        {poem.title}
                      </h3>
                      <p className="text-sm dark:text-gray-300 mb-2">
                        Ghazal #{poem.ghazalNumber}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded">
                          {poem.theme[0]}
                        </span>
                        <span className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded">
                          {poem.difficulty}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Recipes */}
            {dashboard.recent.recipes.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center">
                    <FaUtensils className="mr-2 text-orange-500" />
                    Recent Persian Recipes
                  </h2>
                  <Link
                    to="/cultural/recipes"
                    className="text-orange-500 hover:text-orange-600 font-medium"
                  >
                    View All
                  </Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {dashboard.recent.recipes.map((recipe: any) => {
                    // Map backend recipe data to frontend recipe IDs
                    const recipeIdMap: { [key: string]: string } = {
                      'Ghormeh Sabzi': 'ghormeh-sabzi',
                      'Fesenjan': 'fesenjan',
                      'Chelo Kabab': 'chelo-kabab',
                      'Tahchin': 'tahchin',
                      'Ash Reshteh': 'ash-reshteh'
                    };
                    
                    const recipeId = recipeIdMap[recipe.title] || recipe._id;
                    
                    return (
                      <Link
                        key={recipe._id}
                        to={`/cultural/recipes/${recipeId}`}
                        className="block p-4 rounded-lg border border-gray-100 dark:border-gray-700 hover:border-orange-300 transition-colors"
                      >
                        <h3 className="font-semibold text-gray-800 dark:text-white mb-2">
                          {recipe.title}
                        </h3>
                        <p className="text-sm dark:text-gray-300 mb-2">
                          {recipe.farsiTitle}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs bg-orange-50 text-orange-700 px-2 py-1 rounded">
                            {recipe.category}
                          </span>
                          <span className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded">
                            {recipe.difficulty}
                          </span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Recent Holidays */}
            {dashboard.recent.holidays.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center">
                    <FaCalendarAlt className="mr-2 text-green-500" />
                    Recent Holiday Packs
                  </h2>
                  <Link
                    to="/cultural/holidays"
                    className="text-blue-500 hover:text-blue-600 font-medium"
                  >
                    View All
                  </Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {dashboard.recent.holidays.map((holiday: any) => (
                    <Link
                      key={holiday._id}
                      to={`/cultural/holidays/${holiday._id}`}
                      className="block p-4 rounded-lg border border-gray-100 dark:border-gray-700 hover:border-blue-300 transition-colors"
                    >
                      <h3 className="font-semibold text-gray-800 dark:text-white mb-2">
                        {holiday.title}
                      </h3>
                      <p className="text-sm dark:text-gray-300 mb-2">
                        {holiday.farsiTitle}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-1 rounded">
                          {holiday.holiday}
                        </span>
                        <span className="text-xs bg-teal-50 text-teal-700 px-2 py-1 rounded">
                          {holiday.date}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Cultural Tips */}
        <div className="mt-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl p-8 text-white">
          <div className="text-center">
            <FaStar className="text-4xl mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-4 text-white">Cultural Learning Tips</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div>
                <h3 className="font-semibold mb-2 text-white">Start with Daily Life</h3>
                <p className="text-white">Begin with Tehran lessons to learn practical conversations</p>
              </div>
              <div>
                <h3 className="font-semibold mb-2 text-white">Explore Poetry</h3>
                <p className="text-white">Read Hafez to understand Persian literary traditions</p>
              </div>
              <div>
                <h3 className="font-semibold mb-2 text-white">Cook & Learn</h3>
                <p className="text-white">Practice Persian while cooking traditional dishes</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cultural; 